import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

// Tell axios to send cookies with every request
axios.defaults.withCredentials = true;
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined banner messages
const PREDEFINED_MESSAGES = [
  {
    id: 'construction',
    label: 'Under Construction',
    message: "Site Under Construction: We're currently updating our website. Some features and content may be incomplete, outdated, or inaccurate. Thank you for your patience as we improve your experience!"
  },
  {
    id: 'maintenance',
    label: 'Scheduled Maintenance',
    message: "Scheduled Maintenance: Our website is undergoing scheduled maintenance. Some features may be temporarily unavailable. We'll be back shortly!"
  },
  {
    id: 'new_features',
    label: 'New Features Coming',
    message: "Exciting Updates Coming Soon: We're adding new features to serve you better. Stay tuned for improvements to your browsing experience!"
  },
  {
    id: 'custom',
    label: 'Custom Message',
    message: ''
  }
];

export default function AdminBanner() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [bannerSettings, setBannerSettings] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('construction');
  const [customMessage, setCustomMessage] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // The backend will handle the password check and set a secure httpOnly cookie.
      await axios.post(`${API}/login`, { password });
      setIsAuthenticated(true);
      toast.success('Login successful');
      fetchBannerSettings();
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid password');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };


  const fetchBannerSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/banner-settings`);
      setBannerSettings(response.data);
      setIsActive(response.data.is_active);
      
      // Check if message matches any predefined template
      const matchedTemplate = PREDEFINED_MESSAGES.find(
        (t) => t.message === response.data.message
      );
      
      if (matchedTemplate) {
        setSelectedTemplate(matchedTemplate.id);
      } else {
        setSelectedTemplate('custom');
        setCustomMessage(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching banner settings:', error);
      toast.error('Failed to load banner settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const selectedMsg = PREDEFINED_MESSAGES.find((t) => t.id === selectedTemplate);
      const message = selectedTemplate === 'custom' ? customMessage : selectedMsg.message;

      if (!message.trim()) {
        toast.error('Please enter a banner message');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/banner-settings`, {
        message,
        is_active: isActive
      });

      setBannerSettings(response.data);
      toast.success('Banner settings updated successfully');
    } catch (error) {
      console.error('Error updating banner settings:', error);
      toast.error('Failed to update banner settings');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4" data-testid="admin-login-form">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[#1e3a8a]">
              Admin Login - Banner Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="admin-login-btn"
                type="submit"
                className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#1e3a8a] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Banner Management
          </h1>
          <p className="text-slate-600">Control the site-wide banner message and visibility</p>
        </div>

        {/* Banner Preview */}
        {bannerSettings && isActive && (
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-lg">Banner Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 rounded-lg">
                <div className="flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-center">
                    {selectedTemplate === 'custom' ? customMessage : 
                      PREDEFINED_MESSAGES.find((t) => t.id === selectedTemplate)?.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Banner Settings */}
        <Card data-testid="banner-settings-card">
          <CardHeader>
            <CardTitle>Banner Settings</CardTitle>
            <CardDescription>Manage the announcement banner shown to all visitors</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Banner Status Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="banner-active" className="text-base font-semibold">
                  Banner Status
                </Label>
                <p className="text-sm text-slate-600 mt-1">
                  {isActive ? 'Banner is currently visible to all users' : 'Banner is currently hidden'}
                </p>
              </div>
              <Switch
                id="banner-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                data-testid="banner-active-toggle"
              />
            </div>

            {/* Message Template Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="message-template">Message Template</Label>
              <Select
                value={selectedTemplate}
                onValueChange={setSelectedTemplate}
              >
                <SelectTrigger data-testid="message-template-select">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {PREDEFINED_MESSAGES.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Message Input */}
            {selectedTemplate === 'custom' ? (
              <div className="space-y-2">
                <Label htmlFor="custom-message">Custom Message</Label>
                <Textarea
                  id="custom-message"
                  data-testid="custom-message-textarea"
                  placeholder="Enter your custom banner message..."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={4}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Selected Message</Label>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-700">
                    {PREDEFINED_MESSAGES.find((t) => t.id === selectedTemplate)?.message}
                  </p>
                </div>
              </div>
            )}

            {/* Save Button */}
            <Button
              data-testid="save-banner-btn"
              onClick={handleSave}
              disabled={loading}
              className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white py-6 text-lg"
            >
              {loading ? 'Saving...' : 'Save Banner Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-[#1e3a8a] mb-2">How to Use:</h3>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• <strong>Toggle Banner:</strong> Use the switch to show/hide the banner site-wide</li>
              <li>• <strong>Choose Template:</strong> Select from predefined messages or create custom text</li>
              <li>• <strong>Preview:</strong> See how the banner will appear before saving</li>
              <li>• <strong>Save Changes:</strong> Click save to update the banner for all visitors</li>
              <li>• <strong>Note:</strong> Users cannot close this banner - only you can disable it</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
